import { computed, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { APP_TITLE } from '../constants/app.constants';
import { NAVIGATION_ITEMS, NavigationItem } from '../constants/navigation.constants';
import { UserPermission } from '../constants/permission.constants.enum';
import { AccessService } from './access.service';
import { UserService } from './user.service';

@Injectable({
  providedIn: 'root'
})
export class NavigationService {

  navItems: Signal<NavigationItem[]>;

  flattenedNavItems: NavigationItem[] = [];

  navItems$: Observable<NavigationItem[]>;

  currentNavItem: WritableSignal<NavigationItem | null> = signal(null);

  unauthorizedNavItem: WritableSignal<NavigationItem | null> = signal(null);

  pageTitle = signal('');

  showFacility = computed(() => this.currentNavItem()?.showFacility === true);

  constructor(
    private user: UserService,
    private access: AccessService,
    private titleService: Title,
    private router: Router,
  ) {
    this.navItems = computed(() => {
      const permissions = this.user.permissions();
      if (!permissions.length) return [];

      if (!this.user.profileSettled()) return [];

      return this.configureNavItems(NAVIGATION_ITEMS, permissions);
    });

    this.navItems$ = toObservable(this.navItems).pipe(
      filter(items => items.length > 0)
    );

    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
    ).subscribe(this.setPageTitleFromNavEndEvent);
  }

  setPageTitleFromNavEndEvent = (e: NavigationEnd ): void => {
    const path = e.urlAfterRedirects.slice(1);
    this.currentNavItem.set(this.findNavItemForPath(this.navItems(), path));
    const env = environment.environmentLabel !== 'prod' ? ' (' + environment.environmentLabel.toLocaleUpperCase() + ')' : '';
    const page = this.currentNavItem() ? `${this.currentNavItem()?.pageTitle} | ` : '';
    const title = `${page}${APP_TITLE}${env}`;
    this.titleService.setTitle(title);
    this.pageTitle.set(this.currentNavItem()?.title ?? '');
  }

  configureNavItems(items: NavigationItem[], permissions: UserPermission[]): NavigationItem[] {
    this.flattenedNavItems = [];
    const setVisibility = (item: NavigationItem) => {
        const groupAllows = Array.isArray(item.visibleTo)
          ? this.hasAnyPermission(item.visibleTo)
          : typeof item.visible !== 'undefined' ? item.visible : true;

        // Independent axes, and an item has to satisfy all of them. The Entra group decides
        // whether the feature exists for this user at all; the facility permission grid decides
        // whether they may actually read it.
        //
        // The two grid gates differ only in scope. A Tracker is checked against the *selected*
        // facility, so the menu follows the facility selector. Reports and Administration are
        // checked against *any* facility, because those pages carry no selector - see
        // AccessService.canReadAnyFacility.
        //
        // Both apply to ISADMIN as well, unlike every other visibility rule here: hasAnyPermission
        // short-circuits true for admins, but these are ANDed after it. An admin with no OMT/SCT
        // row would otherwise land on a Tracker the API returns nothing for. Drop the two `&&`
        // clauses to restore the blanket admin bypass.
        const trackerAllows = item.requiresTracker === undefined
          || this.access.canReadTracker(item.requiresTracker);

        const permissionAllows = item.requiresPermission === undefined
          || this.access.canReadAnyFacility(item.requiresPermission);

        item.visible = groupAllows && trackerAllows && permissionAllows;
        addToFlattenedNav(item);
        return item;
    }
    const addToFlattenedNav = (item: NavigationItem): void => {
      if (item.visible && !item.separator && !Array.isArray(item.items)) this.flattenedNavItems.push(item);
    }
    const itemsWithSeparators: NavigationItem[] = []
    items.map(item => {
        setVisibility(item);
        if (Array.isArray(item.items)) {
            item.items = item.items.map(setVisibility)
            // A group whose children are now all hidden would render as a bare label with nothing
            // under it - which is what hiding a Tracker leaves behind.
            if (!item.items.some(child => child.visible)) item.visible = false;
        }
        itemsWithSeparators.push(item);
        if (Array.isArray(item.items) && item.items.filter(item => item.visible).length) {
          itemsWithSeparators.push({
            separator: true
          })
        }
    });
    return itemsWithSeparators;
  }
  
  hasAnyPermission = (itemPermissions: UserPermission[]): boolean => {
    let allowed = false;
    itemPermissions.map((itemPermission: UserPermission) => {
        if (this.user.permissions().includes(itemPermission)) allowed = true;
    });
    return allowed;
  }

  findNavItemForPath = (items: NavigationItem[], path?: string): NavigationItem | null => {
    let match: NavigationItem | null = null;
    items.some(item => {
      if (item.routerLink === path) {
        match = item;
        return true;
      } else if (Array.isArray(item.items)) {
        item.items.some(childItem => {
          if (childItem.routerLink === path) {
            match = childItem;
            return true;
          }
          return false;
        });
      }
      return false;
    });
    return match;
  }
}
